/**
 * Single-venue scope (#689, spec section 4: "limit the outcome markets to a single venue").
 *
 * `venue` is the outcomeMeta field naming the deployer that registered an outcome (docs/VENUES.md). A build can be
 * scoped to one venue per environment; testers can override the scope at runtime unless the build locks it.
 * Pure helpers only (no React). The store + hook live in ./useVenueScope.ts.
 *
 * Precedence (highest first):
 *   1. build-time VITE_VENUE_<ENV> when VITE_VENUE_OVERRIDE is not "1"  -> locked
 *   2. URL ?venue=<id> | ?venue=all                                     (consumed into storage on load)
 *   3. stored override (localStorage hip4.venue.<env>)
 *   4. build-time VITE_VENUE_<ENV> as the default (override allowed)
 *   5. null = all venues
 */
import { NO_VENUE } from '../catalog/catalog';
import type { EnvName } from '../config/env';
import { t } from '../i18n/i18n';

/** URL / storage value meaning "explicitly all venues" (beats a build default when overrides are allowed). */
export const ALL_VENUES = 'all';
export const VENUE_PARAM = 'venue';
export const VENUE_STORAGE_PREFIX = 'hip4.venue.';

export interface VenueBuildConfig {
  readonly testnet: string | null;
  readonly mainnet: string | null;
  /** VITE_VENUE_OVERRIDE=1: a build venue is only a default, testers may change it. */
  readonly overrideAllowed: boolean;
}

export type VenueSource = 'build' | 'url' | 'stored' | 'default' | 'none';

export interface ResolvedVenue {
  /** null = all venues. */
  venue: string | null;
  /** True when a build-time venue is fixed and the override UI is hidden. */
  isLocked: boolean;
  source: VenueSource;
}

/** Trimmed venue id, or null for empty / "all" / non-strings. */
export function normalizeVenue(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  if (!t || t.toLowerCase() === ALL_VENUES) return null;
  return t;
}

/**
 * Raw override value: undefined = not set, ALL_VENUES = explicitly all, otherwise a venue id.
 * Invalid input (empty string, whitespace) counts as not set.
 */
export function parseOverride(v: string | null | undefined): string | undefined {
  if (v === null || v === undefined) return undefined;
  const t = v.trim();
  if (!t) return undefined;
  return t.toLowerCase() === ALL_VENUES ? ALL_VENUES : t;
}

export function readBuildVenueConfig(env: Record<string, unknown> = import.meta.env): VenueBuildConfig {
  return {
    testnet: normalizeVenue(env.VITE_VENUE_TESTNET),
    mainnet: normalizeVenue(env.VITE_VENUE_MAINNET),
    overrideAllowed: String(env.VITE_VENUE_OVERRIDE ?? '').trim() === '1',
  };
}

export function resolveVenueScope(input: {
  build: string | null;
  overrideAllowed: boolean;
  url?: string;
  stored?: string;
}): ResolvedVenue {
  const { build, overrideAllowed } = input;
  if (build !== null && !overrideAllowed) return { venue: build, isLocked: true, source: 'build' };
  if (input.url !== undefined) return { venue: normalizeVenue(input.url), isLocked: false, source: 'url' };
  if (input.stored !== undefined) return { venue: normalizeVenue(input.stored), isLocked: false, source: 'stored' };
  if (build !== null) return { venue: build, isLocked: false, source: 'default' };
  return { venue: null, isLocked: false, source: 'none' };
}

export function storageKey(env: EnvName): string {
  return VENUE_STORAGE_PREFIX + env;
}

export function loadStoredOverride(env: EnvName, storage: Pick<Storage, 'getItem'> | undefined = safeStorage()): string | undefined {
  try {
    return parseOverride(storage?.getItem(storageKey(env)));
  } catch {
    return undefined;
  }
}

/** undefined clears the override (back to the build default). Storage errors are ignored. */
export function saveStoredOverride(
  env: EnvName,
  value: string | undefined,
  storage: Pick<Storage, 'setItem' | 'removeItem'> | undefined = safeStorage(),
): void {
  try {
    if (value === undefined) storage?.removeItem(storageKey(env));
    else storage?.setItem(storageKey(env), value);
  } catch {
    /* private mode / blocked storage: the override just won't persist */
  }
}

function safeStorage(): Storage | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------------------------
// Filtering helpers: every place that lists outcomes goes through these.
// ---------------------------------------------------------------------------------------------

/** Outcome venue key: the venue id, or NO_VENUE for protocol outcomes without one (e.g. "Recurring"). */
export function venueKeyOf(venue: string | undefined | null): string {
  return venue || NO_VENUE;
}

/** True when an outcome with this venue is in scope. scope null = everything is in scope. */
export function inVenue(venue: string | undefined | null, scope: string | null): boolean {
  return scope === null || venueKeyOf(venue) === scope;
}

export function scopeOutcomes<T extends { venue?: string }>(outcomes: readonly T[], scope: string | null): T[] {
  return scope === null ? [...outcomes] : outcomes.filter((o) => inVenue(o.venue, scope));
}

/** outcome id -> venue key, for rows that only carry a coin / outcome id. */
export function venueIndex(outcomes: readonly { outcome: number; venue?: string }[] | undefined): Map<number, string> {
  const m = new Map<number, string>();
  for (const o of outcomes ?? []) m.set(o.outcome, venueKeyOf(o.venue));
  return m;
}

/**
 * In scope for an outcome id. Unknown ids (settled outcomes drop out of outcomeMeta, or meta not loaded yet)
 * return `unknown` so callers decide: lists show them, the order ticket refuses them.
 */
export function outcomeInVenue(
  index: ReadonlyMap<number, string>,
  outcome: number,
  scope: string | null,
): boolean | 'unknown' {
  if (scope === null) return true;
  const v = index.get(outcome);
  if (v === undefined) return 'unknown';
  return v === scope;
}

/**
 * Questions (categorical groups) in scope: a question is kept when any of its outcomes is in scope. On live data
 * every question's outcomes share one venue (checked 2026-10-16, testnet 120 / mainnet 32 questions).
 */
export function scopeQuestions<Q extends { namedOutcomes: number[]; fallbackOutcome: number }>(
  questions: readonly Q[],
  index: ReadonlyMap<number, string>,
  scope: string | null,
): Q[] {
  if (scope === null) return [...questions];
  return questions.filter((q) => [...q.namedOutcomes, q.fallbackOutcome].some((id) => index.get(id) === scope));
}

/** Display name for a venue key. */
export function venueLabel(v: string | null): string {
  if (v === null) return t('venue.all');
  return v === NO_VENUE ? t('venue.none') : v;
}
